/**
 * Ranked type-ahead filtering shared by every combobox (DatalistSelect,
 * AccountPicker).
 *
 * Score (lower = better): exact code · name prefix · word prefix · name
 * contains · code contains · hint (sub) contains. Ties sort by weight
 * (higher first — e.g. how often an option is used) when `getWeight` is
 * given, then by label.
 */

export const MAX_ROWS = 60;

const WORD_SPLIT = /[\s\-/().,]+/;
const lc = (v) => (v == null ? "" : String(v).toLowerCase());

const defaultLabel = (o) => o.label;
const defaultCode = (o) => o.code;

/** Pre-lowercase once per options array; rank many queries against it. */
export function buildRankIndex(options, { getLabel = defaultLabel, getCode = defaultCode, getSub, getWeight } = {}) {
    return options.map((o) => ({
        o,
        weight: getWeight ? Number(getWeight(o)) || 0 : 0,
        label: lc(getLabel(o)),
        code: lc(getCode ? getCode(o) : ""),
        sub: getSub ? lc(getSub(o)) : "",
    }));
}

/**
 * `anchor(option) → boolean` (unfiltered only): when the current pick sits
 * past the first window, start the window a few rows above it so it stays
 * visible and highlighted instead of silently falling off the list.
 */
export function rankIndex(index, query, max = MAX_ROWS, anchor) {
    const q = lc(query).trim();
    if (!q) {
        let start = 0;
        if (anchor && index.length > max) {
            const at = index.findIndex((e) => anchor(e.o));
            if (at >= max) start = Math.min(at - 10, index.length - max);
        }
        return index.slice(start, start + max).map((e) => e.o);
    }
    const scored = [];
    for (const e of index) {
        let score = -1;
        if (e.code && e.code === q) score = 0;
        else if (e.label.startsWith(q)) score = 1;
        else if (e.label.split(WORD_SPLIT).some((w) => w.startsWith(q))) score = 2;
        else if (e.label.includes(q)) score = 3;
        else if (e.code && e.code.includes(q)) score = 4;
        else if (e.sub && e.sub.includes(q)) score = 5;
        if (score >= 0) scored.push({ e, score });
    }
    scored.sort((a, b) => a.score - b.score || b.e.weight - a.e.weight || a.e.label.localeCompare(b.e.label));
    return scored.slice(0, max).map((s) => s.e.o);
}

/** Count of matches (uncapped) — for the "N more, keep typing" footer. */
export function countMatches(index, query) {
    const q = lc(query).trim();
    if (!q) return index.length;
    let n = 0;
    for (const e of index) {
        if (e.label.includes(q) || (e.code && e.code.includes(q)) || (e.sub && e.sub.includes(q))) n++;
    }
    return n;
}

export default function rankOptions(options, query, accessors) {
    return rankIndex(buildRankIndex(options, accessors), query, accessors?.max);
}
